import React, { useMemo, useState } from "react";
import * as FiIcons from "react-icons/fi";
import {
  SettingsActions,
  SettingsCard,
  SettingsField,
  SettingsNotice,
} from "./SettingsPanel";

const { FiBriefcase, FiEdit2, FiLock } = FiIcons;

const groups = [
  {
    title: "General Information",
    fields: [
      ["Business Name", "organization.name", "text", true],
      ["Company Name", "clientProfile.company_name", "text", true],
      ["Business Type", "clientProfile.business_type", "text", true],
      ["Industry", "clientProfile.industry", "text", true],
      ["Year Started", "clientProfile.year_started", "number", true],
      ["Website", "organization.website", "url", true],
    ],
  },
  {
    title: "Contact Information",
    fields: [
      ["Business Email", "clientProfile.business_email", "email", true],
      ["Business Phone", "clientProfile.phone", "tel", true],
      ["Business Address", "clientProfile.business_address", "text", true],
    ],
  },
  {
    title: "Business Details",
    fields: [
      ["Monthly Revenue", "clientProfile.monthly_revenue", "text", true],
      ["Number of Employees", "clientProfile.employee_count", "text", true],
      ["Accepts Online Payments", "clientProfile.accepts_online_payments", "boolean", true],
      ["Uses Accounting Software", "clientProfile.uses_software", "boolean", true],
      ["Bookkeeping Manager", "clientProfile.bookkeeping_manager", "text", true],
    ],
  },
  {
    title: "Accounting Information",
    fields: [
      ["Country", "clientProfile.country", "text", false],
      ["Currency", "clientProfile.currency", "text", false],
      ["Fiscal Year", "clientProfile.fiscal_year", "text", false],
      ["Tax ID", "clientProfile.tax_id", "text", false],
    ],
  },
];

const getValue = (settings, path) => {
  const [source, key] = path.split(".");
  return settings?.[source]?.[key] ?? "";
};

const displayValue = (value, type) => {
  if (type === "boolean") {
    return value ? "Yes" : "No";
  }

  return value === "" || value == null
    ? "Not provided"
    : String(value);
};

export default function BusinessProfileSettings({
  settings,
  onSaved,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const fields = useMemo(
    () => groups.flatMap((group) => group.fields),
    []
  );
  const canEdit = settings?.canEditBusiness === true;
  const hasBusinessProfile = Boolean(settings?.clientProfile);

  const startEditing = () => {
    const values = {};
    fields.forEach(([, path, type]) => {
      values[path] = getValue(settings, path);
      if (type === "boolean") {
        values[path] = Boolean(values[path]);
      }
    });
    setDraft(values);
    setMessage(null);
    setEditing(true);
  };

  const cancelEditing = () => {
    setDraft({});
    setMessage(null);
    setEditing(false);
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      const organizationChanges = {};
      const clientProfileChanges = {};

      fields.forEach(([, path, , editable]) => {
        if (!editable || draft[path] === getValue(settings, path)) return;
        const [source, key] = path.split(".");
        if (source === "organization") {
          organizationChanges[key] = draft[path];
        } else {
          clientProfileChanges[key] = draft[path];
        }
      });

      const updated = await onSaved({
        organizationChanges,
        clientProfileChanges,
      });

      const next = {};
      fields.forEach(([, path, type]) => {
        next[path] = getValue(updated, path);
        if (type === "boolean") next[path] = Boolean(next[path]);
      });
      setDraft(next);
      setEditing(false);
      setMessage({
        kind: "success",
        text: "Business profile changes were saved.",
      });
    } catch (error) {
      setMessage({
        kind: "error",
        text: error.message || "Unable to save business profile.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsCard
      title="Business Profile"
      description="Business information for the selected client organization."
      icon={FiBriefcase}
    >
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-slate-500">
          {canEdit
            ? "Editing is available for the verified organization owner."
            : "Your current organization membership is read-only for business details."}
        </p>
        {canEdit && hasBusinessProfile && !editing && (
          <button
            type="button"
            onClick={startEditing}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            <FiEdit2 aria-hidden="true" />
            Edit profile
          </button>
        )}
      </div>

      {!hasBusinessProfile && (
        <SettingsNotice>
          No client business profile was returned for this organization. Organization details remain available below.
        </SettingsNotice>
      )}

      {message && (
        <div className="mb-4">
          <SettingsNotice kind={message.kind}>
            {message.text}
          </SettingsNotice>
        </div>
      )}

      {!canEdit && (
        <div className="mb-4 flex items-start gap-2 rounded-xl bg-slate-50 p-3 text-xs leading-5 text-slate-600">
          <FiLock className="mt-0.5 shrink-0" />
          Business fields are read-only for your current membership role.
        </div>
      )}

      <form onSubmit={save} className="space-y-6">
        {groups.map((group) => (
          <div key={group.title}>
            <h3 className="mb-3 text-sm font-semibold text-slate-800">
              {group.title}
            </h3>
            <div className="grid gap-4 sm:grid-cols-2">
              {group.fields.map(([label, path, type, editable]) => {
                const value = editing ? draft[path] : getValue(settings, path);
                const isEditable = editing && editable;

                return (
                  <SettingsField key={path} label={label}>
                    {isEditable && type === "boolean" ? (
                      <select
                        value={value ? "true" : "false"}
                        onChange={(event) =>
                          setDraft((current) => ({
                            ...current,
                            [path]: event.target.value === "true",
                          }))
                        }
                        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                      >
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    ) : isEditable ? (
                      <input
                        type={type}
                        min={type === "number" ? 1800 : undefined}
                        max={type === "number" ? new Date().getFullYear() : undefined}
                        value={value ?? ""}
                        onChange={(event) =>
                          setDraft((current) => ({
                            ...current,
                            [path]: event.target.value,
                          }))
                        }
                        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                      />
                    ) : (
                      <span className="mt-1.5 block min-h-10 break-words rounded-xl bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
                        {displayValue(value, type)}
                      </span>
                    )}
                  </SettingsField>
                );
              })}
            </div>
          </div>
        ))}

        {editing && (
          <SettingsActions
            onCancel={cancelEditing}
            saving={saving}
            disabled={!hasBusinessProfile}
          />
        )}
      </form>
    </SettingsCard>
  );
}